import React, { useState } from 'react';
import styled from 'styled-components';
import { useQueryClient } from '@tanstack/react-query';
import { colors } from '@/constants/colors';
import { toast } from 'react-toastify';
import Input from '../UI/Input';
import Btn from '../UI/Btn';
import { useUIStore } from '@/store/useUIStore';
import {
   createResellerInquiryApi,
   type ApiPublicReseller,
} from '@/api/vendo';

// Fixed retail price per credit when buying from a reseller (₱500). The
// reseller's margin over their ₱350 buy-in is baked in; there's no per-
// reseller price knob. Mirror of the distributor Inquire modal, minus the
// on-platform payment accounts — resellers settle by message after they
// approve, so no proof is required up front.
const RESELLER_PRICE_PER_CREDIT = 500;

interface Props {
   /**
    * `initialCredits` is the quantity already chosen on the seller page's
    * checkout panel — without it the form would reset to 1 and quietly
    * discard what the shopper picked.
    */
   data?: ApiPublicReseller & { initialCredits?: number };
}

const InquireReseller = ({ data }: Props) => {
   const { handleClose } = useUIStore();
   const qc = useQueryClient();
   const reseller = data;

   const [credits, setCredits] = useState(
      data?.initialCredits && data.initialCredits > 0
         ? String(data.initialCredits)
         : '1'
   );
   const [note, setNote] = useState('');
   const [isLoading, setIsLoading] = useState(false);

   const available = reseller?.availableCredits ?? 0;
   const creditsNum = Number(credits) || 0;
   const total = creditsNum * RESELLER_PRICE_PER_CREDIT;
   const overLimit = creditsNum > available;

   const blockReason = !reseller
      ? 'Reseller not found'
      : available === 0
      ? 'This reseller has no credits available right now — check back later'
      : creditsNum <= 0
      ? 'Enter how many credits you want'
      : overLimit
      ? `Only ${available.toLocaleString()} credit${
           available === 1 ? '' : 's'
        } available from this reseller`
      : null;
   const canSubmit = !isLoading && !blockReason;

   const handleSubmit = async () => {
      if (!canSubmit || !reseller) return;
      setIsLoading(true);
      try {
         await createResellerInquiryApi({
            resellerId: reseller._id,
            requestedCredits: creditsNum,
            notes: note.trim() || undefined,
         });
         qc.invalidateQueries({ queryKey: ['vendo', 'inquiries'] });
         qc.invalidateQueries({ queryKey: ['vendo', 'public-resellers'] });
         toast.success(
            `Request sent to ${reseller.displayName} · ${creditsNum} credit${
               creditsNum === 1 ? '' : 's'
            }`,
            { autoClose: 3000 }
         );
         handleClose();
      } catch (err: any) {
         toast.error(err?.response?.data?.error || 'Failed to send request');
      } finally {
         setIsLoading(false);
      }
   };

   return (
      <Wrapper>
         <ScrollArea>
            <SellerSummary>
               <SummaryLabel>Buying from</SummaryLabel>
               <SummaryName>{reseller?.displayName ?? 'Reseller'}</SummaryName>
               <SummaryMeta>
                  ₱{RESELLER_PRICE_PER_CREDIT}/credit
                  {reseller?.upline ? ` · via ${reseller.upline}` : ''} ·{' '}
                  {available.toLocaleString()} available
               </SummaryMeta>
            </SellerSummary>

            <FieldWrap>
               <Input
                  label="How many credits? *"
                  name="credits"
                  value={credits}
                  placeholder="1"
                  error={overLimit}
                  onChange={(e) =>
                     setCredits(e.target.value.replace(/[^0-9]/g, ''))
                  }
               />
               <Hint>
                  No minimum — you can buy a single credit. Available:{' '}
                  <strong>{available.toLocaleString()}</strong>.
               </Hint>
               {overLimit && (
                  <FieldError>
                     You can't request more than {available.toLocaleString()} credit
                     {available === 1 ? '' : 's'} from this reseller.
                  </FieldError>
               )}
            </FieldWrap>

            {(reseller?.contactPhone || reseller?.facebookUrl) && (
               <ContactBlock>
                  <ContactTitle>Contact {reseller.displayName}</ContactTitle>
                  {reseller.contactPhone && (
                     <ContactRow>
                        <ContactLbl>📞 Phone</ContactLbl>
                        <ContactVal>{reseller.contactPhone}</ContactVal>
                     </ContactRow>
                  )}
                  {reseller.facebookUrl && (
                     <ContactRow>
                        <ContactLbl>📘 Facebook</ContactLbl>
                        <ContactLink
                           href={reseller.facebookUrl}
                           target="_blank"
                           rel="noopener noreferrer"
                        >
                           {reseller.facebookUrl}
                        </ContactLink>
                     </ContactRow>
                  )}
               </ContactBlock>
            )}

            <PayNote>
               After you send this request, <strong>{reseller?.displayName}</strong>{' '}
               confirms it and messages you where to pay (GCash / bank). Credits land
               in your wallet once they mark it paid.
            </PayNote>

            <FieldWrap>
               <Label>Note (optional)</Label>
               <Textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="e.g. your GCash name, or when you'll pay"
               />
            </FieldWrap>
         </ScrollArea>

         <Footer>
            <FooterTotals>
               <FooterTotalsRow>
                  <FooterTotalsLbl>
                     {creditsNum} × ₱{RESELLER_PRICE_PER_CREDIT}
                  </FooterTotalsLbl>
                  <FooterTotalsVal>₱{total.toLocaleString()}</FooterTotalsVal>
               </FooterTotalsRow>
               {blockReason && <FooterBlock>{blockReason}</FooterBlock>}
            </FooterTotals>
            <BtnRow>
               <div onClick={handleClose}>
                  <Btn title="Cancel" type="outline" size="small" />
               </div>
               <SendBtn type="button" disabled={!canSubmit} onClick={handleSubmit}>
                  <SendMain>{isLoading ? 'Sending…' : 'Send Request'}</SendMain>
                  {!isLoading && creditsNum > 0 && (
                     <SendSub>
                        {creditsNum} credit{creditsNum === 1 ? '' : 's'} · ₱
                        {total.toLocaleString()}
                     </SendSub>
                  )}
               </SendBtn>
            </BtnRow>
         </Footer>
      </Wrapper>
   );
};

export default InquireReseller;

const Wrapper = styled.div`
   width: 480px;
   max-width: 100%;
   display: flex;
   flex-direction: column;
   @media (max-width: 768px) {
      width: 100%;
   }
`;

const ScrollArea = styled.div`
   flex: 1;
   overflow-y: auto;
   display: flex;
   flex-direction: column;
   gap: 12px;
   padding-right: 4px;
`;

const SellerSummary = styled.div`
   padding: 12px 14px;
   background: var(--app-brand-tint);
   border-radius: 10px;
   display: flex;
   flex-direction: column;
   gap: 3px;
`;

const SummaryLabel = styled.p`
   font-size: 10px;
   font-weight: 700;
   color: ${colors.GRAY5};
   text-transform: uppercase;
   letter-spacing: 0.05em;
`;

const SummaryName = styled.p`
   font-size: 16px;
   font-weight: 800;
   color: ${colors.MAIN_DARK};
`;

const SummaryMeta = styled.p`
   font-size: 12px;
   color: ${colors.GRAY3};
`;

const FieldWrap = styled.div`
   display: flex;
   flex-direction: column;
   gap: 4px;
`;

const Hint = styled.p`
   font-size: 11px;
   color: ${colors.GRAY5};
`;

const Label = styled.label`
   font-size: 13px;
   color: ${colors.GRAY1};
   font-weight: 600;
`;

const FieldError = styled.p`
   font-size: 11px;
   color: ${colors.DANGER};
   font-weight: 600;
`;

const Textarea = styled.textarea`
   width: 100%;
   padding: 10px 12px;
   border: 1px solid ${colors.GRAY12};
   border-radius: 6px;
   font-size: 13px;
   font-family: inherit;
   color: ${colors.GRAY_DARK};
   background: var(--app-surface);
   outline: none;
   resize: vertical;
   min-height: 56px;
   &:focus {
      border-color: ${colors.PRIMARY};
   }
`;

const ContactBlock = styled.div`
   padding: 10px;
   background: ${colors.GRAY14};
   border: 1px solid ${colors.GRAY12};
   border-radius: 10px;
   display: flex;
   flex-direction: column;
   gap: 6px;
`;

const ContactTitle = styled.p`
   font-size: 12px;
   font-weight: 800;
   color: ${colors.GRAY_DARK};
   text-transform: uppercase;
   letter-spacing: 0.05em;
`;

const ContactRow = styled.div`
   display: grid;
   grid-template-columns: 92px 1fr;
   gap: 8px;
   align-items: center;
`;

const ContactLbl = styled.span`
   font-size: 11px;
   color: ${colors.GRAY3};
   font-weight: 700;
`;

const ContactVal = styled.span`
   font-size: 13px;
   color: ${colors.MAIN_DARK};
   word-break: break-word;
`;

const ContactLink = styled.a`
   font-size: 13px;
   color: ${colors.PRIMARY};
   font-weight: 600;
   text-decoration: none;
   word-break: break-all;
   &:hover {
      text-decoration: underline;
   }
`;

const PayNote = styled.p`
   font-size: 12px;
   line-height: 1.45;
   color: ${colors.GRAY3};
   background: ${colors.PRIMARY}08;
   border: 1px solid ${colors.PRIMARY}22;
   border-radius: 10px;
   padding: 10px 12px;
`;

const Footer = styled.div`
   flex-shrink: 0;
   border-top: 1px solid ${colors.GRAY13};
   padding-top: 10px;
   margin-top: 10px;
   display: flex;
   flex-direction: column;
   gap: 8px;
`;

const FooterTotals = styled.div`
   display: flex;
   flex-direction: column;
   gap: 2px;
`;

const FooterTotalsRow = styled.div`
   display: flex;
   justify-content: space-between;
   align-items: center;
`;

const FooterTotalsLbl = styled.span`
   font-size: 12px;
   color: ${colors.GRAY3};
   font-weight: 600;
`;

const FooterTotalsVal = styled.span`
   font-size: 18px;
   font-weight: 800;
   color: ${colors.PRIMARY};
`;

const FooterBlock = styled.p`
   font-size: 11px;
   color: ${colors.DANGER};
   font-weight: 600;
`;

const BtnRow = styled.div`
   display: flex;
   justify-content: flex-end;
   align-items: stretch;
   gap: 10px;
`;

const SendBtn = styled.button`
   display: flex;
   flex-direction: column;
   align-items: center;
   justify-content: center;
   gap: 1px;
   min-width: 200px;
   padding: 6px 18px;
   background: linear-gradient(135deg, ${colors.PRIMARY} 0%, #6a4dff 55%, #8b5cf6 100%);
   color: white;
   border: none;
   border-radius: 8px;
   cursor: pointer;
   font-family: inherit;
   &:hover:not(:disabled) {
      opacity: 0.92;
   }
   &:disabled {
      background: ${colors.GRAY10};
      color: ${colors.GRAY1};
      cursor: not-allowed;
   }
`;

const SendMain = styled.span`
   font-size: 13px;
   font-weight: 800;
   line-height: 1.2;
`;

const SendSub = styled.span`
   font-size: 11px;
   font-weight: 600;
   opacity: 0.85;
   line-height: 1.2;
`;
